// render-config.ts — presentation knobs for the dependency timeline.
// Colours use CIQ design tokens (THEMING.md: no raw hex outside src/themes);
// the type scale matches the project's SVG visualisations (13 / 12 / 11).

export interface RenderConfig {
  modalTitle: string;
  modalWidth: string;

  // Coordinate system (SVG viewBox units).
  viewWidth: number;
  labelColumnWidth: number; // left column with row labels
  clusterWidth: number; // right column for duration pill + activity box
  timelinePadding: number;
  bandHeight: number; // header band and each bid-package band

  // Bar / marker dimensions.
  mainTimelineHeight: number;
  smallTimelineHeight: number;
  milestoneTickWidth: number;
  barRadius: number; // gantt-aligned corner radius on timeline bars

  // Layout aliases consumed by layout.ts (kept equal to bandHeight).
  axisHeight: number;
  rowHeight: number;

  // Type scale (px) — aligns with FS_LABEL/FS_DATE used by the schedule viz.
  fonts: {
    family: string;
    name: number; // milestone / bid-package names
    body: number; // dates, box text
    caption: number; // subtitles, small labels
  };

  // CIQ semantic / primitive tokens (resolved from src/themes at runtime).
  palette: {
    panelBg: string;
    divider: string;
    text: string; // primary body text (--ciq-fg)
    textMuted: string; // secondary text — still meets AA contrast
    tick: string;
    mainTimeline: string;
    smallTimeline: string;
    dependency: string;
    arrow: string;
    boxBorder: string;
    dialogStroke: string;
    deleted: string;
    danger: string; // invalid-state feedback (e.g. a drag clamped at a bound)
    surface: string;
    designPackage: string; // brand accent for the design-package entity
    bidPackage: string; // teal accent for the bid-package entity
    outOfScope: string; // faded band for out-of-scope (compressed) time
    gridline: string; // faint month/quarter gridlines on the time scale
  };

  noPlanText: string;
  emptyText: string;
}

export const DEFAULT_RENDER_CONFIG: RenderConfig = {
  modalTitle: "Design Package Dependencies",
  modalWidth: "80%",

  viewWidth: 1200,
  labelColumnWidth: 225,
  clusterWidth: 240,
  timelinePadding: 24,
  bandHeight: 90,

  mainTimelineHeight: 14,
  smallTimelineHeight: 12, // gantt-aligned bar thickness
  milestoneTickWidth: 3,
  barRadius: 3, // slightly softer corners than the default gantt bar, still enterprise-conservative

  axisHeight: 140, // 24px top padding baked in + design block + timeline row
  rowHeight: 114, // taller bid-package rows for breathing room

  fonts: {
    family: "Inter",
    name: 13,
    body: 12,
    caption: 11
  },

  palette: {
    panelBg: "var(--ciq-surface-muted)",
    divider: "var(--ciq-border-subtle)",
    text: "var(--ciq-fg)",
    textMuted: "var(--ciq-fg-muted)",
    tick: "var(--ciq-fg-subtle)",
    mainTimeline: "var(--ciq-gray-400)",
    smallTimeline: "var(--ciq-gray-200)",
    dependency: "var(--ciq-orange-600)",
    arrow: "var(--ciq-gray-300)", // lighter connector so dependency lines read as guides, not foreground
    boxBorder: "var(--ciq-fg)",
    dialogStroke: "var(--ciq-gray-200)",
    deleted: "var(--ciq-danger)",
    danger: "var(--ciq-danger)",
    surface: "var(--ciq-surface)",
    designPackage: "var(--ciq-primary-900)",
    bidPackage: "var(--ciq-teal-700)",
    outOfScope: "var(--ciq-gray-100)",
    gridline: "var(--ciq-gray-200)"
  },

  noPlanText: "No plan yet",
  emptyText: "No Bid Packages linked to this design package yet"
};
